import ajax from '@/lib/ajax';
import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators';
import store from '@/store';
import { AppModule } from '@/store/modules/app';
import ActivityDto from '@/models/Activities/ActivityDto';
import ActivityCategoryDto from '@/models/Activities/ActivityCategoryDto';

export interface IActivitiesListState {
    loading: boolean;
    activities: ActivityDto[];
    categories: ActivityCategoryDto[];
    totalCount: number;
}

interface LoadActivitiesParams {
    page: number;
    pageSize: number;
    activityTypes?: number[];
    statuses?: number[];
    categoryIds?: string[];
    ownerIds?: string[];
    branchIds?: string[];
    deskIds?: string[];
}

interface LoadMyTasksParams {
    page: number;
    pageSize: number;
    statuses?: number[];
    ownerIds?: string[];
    categoryIds?: string[];
}

function buildQueryParams(params: LoadActivitiesParams): string {
    const queryParams: string[] = [];
    queryParams.push(`limit=${params.pageSize}`);
    queryParams.push(`offset=${(params.page - 1) * params.pageSize}`);

    if (params.activityTypes && params.activityTypes.length > 0) {
        params.activityTypes.forEach(type => {
            queryParams.push(`activityTypes=${type}`);
        });
    }

    if (params.statuses && params.statuses.length > 0) {
        params.statuses.forEach(status => {
            queryParams.push(`statuses=${status}`);
        });
    }

    if (params.categoryIds && params.categoryIds.length > 0) {
        params.categoryIds.forEach(id => {
            queryParams.push(`categoryIds=${id}`);
        });
    }

    if (params.ownerIds && params.ownerIds.length > 0) {
        params.ownerIds.forEach(id => {
            queryParams.push(`ownerIds=${id}`);
        });
    }

    if (params.branchIds && params.branchIds.length > 0) {
        params.branchIds.forEach(id => {
            queryParams.push(`branchIds=${id}`);
        });
    }

    if (params.deskIds && params.deskIds.length > 0) {
        params.deskIds.forEach(id => {
            queryParams.push(`deskIds=${id}`);
        });
    }

    return queryParams.join('&');
}

function buildTasksQueryParams(params: LoadMyTasksParams): string {
    const queryParams: string[] = [];
    queryParams.push(`limit=${params.pageSize}`);
    queryParams.push(`offset=${(params.page - 1) * params.pageSize}`);

    if (params.statuses && params.statuses.length > 0) {
        params.statuses.forEach(status => {
            queryParams.push(`statuses=${status}`);
        });
    }

    if (params.ownerIds && params.ownerIds.length > 0) {
        params.ownerIds.forEach(id => {
            queryParams.push(`ownerIds=${id}`);
        });
    }

    if (params.categoryIds && params.categoryIds.length > 0) {
        params.categoryIds.forEach(id => {
            queryParams.push(`categoryIds=${id}`);
        });
    }

    return queryParams.join('&');
}

@Module({ dynamic: true, store, name: 'ActivitiesList', namespaced: true })
class ActivitiesList extends VuexModule implements IActivitiesListState {
    public loading = false;
    public activities: ActivityDto[] = [];
    public categories: ActivityCategoryDto[] = [];
    public totalCount = 0;

    @Action
    public async loadCategories() {
        await ajax.get('/api/v1/agency/activity-categories')
            .then(response => {
                this.context.commit('setCategories', response.data || []);
            })
            .catch(error => {
                AppModule.handleApiError(error);
            });
    }

    @Action
    public async loadActivities(params: LoadActivitiesParams) {
        this.context.commit('setLoading', true);

        const queryParams = buildQueryParams(params);

        try {
            const response = await ajax.get(`/api/v1/agency/activities?${queryParams}`);
            const data = response.data || {};
            const activities = data.activities || [];
            const total = data.total || 0;

            this.context.commit('setActivities', activities);
            this.context.commit('setTotalCount', total);
        } catch (error) {
            AppModule.handleApiError(error);
        } finally {
            this.context.commit('setLoading', false);
        }
    }

    @Action
    public async loadMoreActivities(params: LoadActivitiesParams) {
        const queryParams = buildQueryParams(params);

        try {
            const response = await ajax.get(`/api/v1/agency/activities?${queryParams}`);
            const data = response.data || {};
            const newActivities = data.activities || [];
            const total = data.total || 0;

            this.context.commit('appendActivities', newActivities);
            this.context.commit('setTotalCount', total);
        } catch (error) {
            AppModule.handleApiError(error);
        }
    }

    @Action
    public async loadMyTasks(params: LoadMyTasksParams) {
        this.context.commit('setLoading', true);

        const queryParams = buildTasksQueryParams(params);

        try {
            const response = await ajax.get(`/api/v1/agency/activities/tasks/my?${queryParams}`);
            const data = response.data || {};
            const tasks = data.tasks || [];
            const total = data.total || 0;

            this.context.commit('setActivities', tasks);
            this.context.commit('setTotalCount', total);
        } catch (error) {
            AppModule.handleApiError(error);
        } finally {
            this.context.commit('setLoading', false);
        }
    }

    @Action
    public async loadMoreMyTasks(params: LoadMyTasksParams) {
        const queryParams = buildTasksQueryParams(params);

        try {
            const response = await ajax.get(`/api/v1/agency/activities/tasks/my?${queryParams}`);
            const data = response.data || {};
            const newTasks = data.tasks || [];
            const total = data.total || 0;

            this.context.commit('appendActivities', newTasks);
            this.context.commit('setTotalCount', total);
        } catch (error) {
            AppModule.handleApiError(error);
        }
    }

    @Mutation
    private setLoading(loading: boolean) {
        this.loading = loading;
    }

    @Mutation
    private setActivities(activities: ActivityDto[]) {
        this.activities = activities;
    }

    @Mutation
    private appendActivities(activities: ActivityDto[]) {
        const existingIds = new Set(this.activities.map(a => a.id));
        const newActivities = activities.filter(a => !existingIds.has(a.id));
        this.activities = [...this.activities, ...newActivities];
    }

    @Mutation
    private setCategories(categories: ActivityCategoryDto[]) {
        this.categories = categories;
    }

    @Mutation
    private setTotalCount(count: number) {
        this.totalCount = count;
    }

    @Action
    cleanState() {
        this.context.commit('resetState');
    }

    @Mutation
    resetState() {
        this.loading = false;
        this.activities = [];
        this.categories = [];
        this.totalCount = 0;
    }
}

export const ActivitiesListModule = getModule(ActivitiesList);
